import { useEffect, useRef, useState } from 'react'
import {
    hasCustomers, getSubscription, createSubscription, updateSubscription,
    type LineItem,
} from '../api'
import { useTabs, useDataVersion, useDataVersionUnlessDirty, useDirtyGuard, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useEditLock } from '../layout/useEditLock'
import { BRANDS } from '../lookups'
import { fill, SUBSCRIPTION_REACTIVATE_HINT, SUBSCRIPTION_STALE, STALE_RELOAD, SUBSCRIPTION_STATUS_SINCE } from '../../shared/texts'
import LineItemsEditor from '../components/LineItemsEditor'
import CustomerPicker from '../components/CustomerPicker'
import { checkBeforeSave } from '../forms/checkForm'
import { DEFAULT_FORM_BRAND } from '../../shared/constants'
import { PAGES_SUBSCRIPTION_FORM_TEXTS as T } from '../../shared/texts'
import {
    ABO_PLACEHOLDER_END, ABO_PLACEHOLDER_START, billingDayFor, fillAboPlaceholders,
    formatDayDe, isoDay, parseCalendarDay, periodEnd, toCalendarDay,
} from '../../shared/aboDates'

// A manual abo (source 'manual') is ONE position: product name with placeholders, quantity 1, net
// price (operator, 30.09.2026 — the Ninox model). The tax rate is not set here: the run takes it
// from the customer (reverse charge or the rate in force). The month-end run bills every abo whose
// next billing date is due. PSP abos (Stripe/Mollie) keep their positions as mirrored.

const todayIso = () => isoDay(toCalendarDay(new Date()))
const dayIso = (v?: string) => (v ? String(v).slice(0, 10) : '')

export default function SubscriptionForm({ tabId, id }: { tabId?: string, id?: string }) {
    const { publishChange, closeTab } = useTabs()
    const { toast } = useToast()
    const { can } = useAuth()
    const readOnly = !can('subscriptions', 'write')
    const recordVersion = useDataVersion(id ? `subscriptions:${id}` : '')
    const lock = useEditLock(id ? `subscription:${id}` : null)
    // null while unknown — so the "create one first" notice doesn't flash up during loading.
    const [anyCustomer, setAnyCustomer] = useState<boolean | null>(null)
    const customersVersion = useDataVersion('customers')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const grantedToasted = useRef(false)
    const formRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        if (lock.justGranted && !grantedToasted.current) {
            grantedToasted.current = true
            toast(T.canEditNow)
        }
    }, [lock.justGranted, toast])

    const [customerId, setCustomerId] = useState('')
    const [brand, setBrand] = useState(DEFAULT_FORM_BRAND)
    const [cadence, setCadence] = useState<'monthly' | 'yearly'>('monthly')
    const [mode, setMode] = useState<'auto' | 'draft'>('auto')
    const [status, setStatus] = useState<'active' | 'paused' | 'ended'>('active')
    const [source, setSource] = useState<'manual' | 'stripe' | 'mollie'>('manual')
    const [paidUntil, setPaidUntil] = useState<string | undefined>()
    // Manual abo
    const [productName, setProductName] = useState('')
    const [netPrice, setNetPrice] = useState(0)
    const [startDate, setStartDate] = useState(todayIso())
    const [nextBillingDate, setNextBillingDate] = useState(todayIso())
    // A new abo's next date follows start and rhythm until it is set by hand.
    const [nextTouched, setNextTouched] = useState(false)
    const [billingDay, setBillingDay] = useState<number | undefined>()
    const [lastRunError, setLastRunError] = useState<{ at?: string, message: string } | null>(null)
    // As saved: the status and the day it was paused / ended (manual abos).
    const [savedStatus, setSavedStatus] = useState('active')
    const [statusSince, setStatusSince] = useState('')
    // Bumped after a save: the server may move the next date (back to „aktiv"), so read it again.
    const [reloadKey, setReloadKey] = useState(0)
    // The abo's updatedAt as loaded — sent with the save, which the server refuses if it moved.
    const [loadedAt, setLoadedAt] = useState<string | undefined>()
    const [stale, setStale] = useState(false)
    // PSP abo: the mirrored positions
    const [items, setItems] = useState<LineItem[]>([])
    const [baseline, setBaseline] = useState<string | null>(null) // JSON snapshot of last saved/loaded state

    const manual = source === 'manual'
    const snapshotOf = (v: Record<string, unknown>) => JSON.stringify(v)
    const snapshot = snapshotOf({
        customerId, brand, cadence, mode, status,
        ...(manual ? { productName, netPrice, startDate, nextBillingDate } : { items }),
    })
    const dirty = baseline !== null && snapshot !== baseline
    // Server-side changes (the month-end run moves the next date or records an error, a PSP ends
    // or pauses the abo) arrive without this abo's id — reload on any, unless edits are pending.
    const othersVersion = useDataVersionUnlessDirty('subscriptions', dirty)

    // Re-asked on every customer change, so creating the first customer in another tab unlocks
    // the form without a reload.
    useEffect(() => {
        hasCustomers().then(setAnyCustomer).catch((e) => setError(e?.message ?? T.loadFailed))
    }, [customersVersion])

    // Capture the initial defaults as baseline for a new (unsaved) subscription.
    useEffect(() => {
        if (!id) setBaseline(snapshot)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // New abo: the next date IS the start date (operator, 02.10.2026; the server does the same).
    // Follows the start until it was edited by hand.
    useEffect(() => {
        if (id || nextTouched) return
        if (parseCalendarDay(startDate)) setNextBillingDate(startDate)
    }, [id, nextTouched, startDate])

    useEffect(() => {
        if (!id) return
        getSubscription(id).then((s) => {
            const src = s.source ?? 'manual'
            const first = s.items?.[0]
            setCustomerId(s.customerId); setBrand(s.brand); setCadence(s.cadence); setMode(s.mode)
            setStatus(s.status); setSource(src)
            setPaidUntil(s.currentPeriodEnd)
            setItems(s.items ?? [])
            setProductName(first?.description ?? ''); setNetPrice(first?.unitPrice ?? 0)
            setStartDate(dayIso(s.startDate)); setNextBillingDate(dayIso(s.nextBillingDate))
            setBillingDay(s.billingDay)
            setLastRunError(s.lastRunError ? { at: s.lastRunErrorAt, message: s.lastRunError } : null)
            setSavedStatus(s.status); setStatusSince(dayIso(s.statusSince))
            setLoadedAt(s.updatedAt); setStale(false); setError('')
            setNextTouched(true)
            setBaseline(snapshotOf({
                customerId: s.customerId, brand: s.brand, cadence: s.cadence, mode: s.mode, status: s.status,
                ...(src === 'manual'
                    ? { productName: first?.description ?? '', netPrice: first?.unitPrice ?? 0, startDate: dayIso(s.startDate), nextBillingDate: dayIso(s.nextBillingDate) }
                    : { items: s.items ?? [] }),
            }))
        }).catch((e) => setError(e?.message ?? T.loadFailed))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, recordVersion, reloadKey, othersVersion])

    const locked = !lock.canEdit
    const disabled = locked || busy || readOnly

    // The name as it will stand on the next invoice — the placeholders filled for the next period.
    const next = parseCalendarDay(nextBillingDate)
    const preview = next && productName.trim()
        ? fillAboPlaceholders(productName.trim(), next, periodEnd(next, cadence, billingDayFor(next, billingDay ?? parseCalendarDay(startDate)?.getUTCDate())))
        : ''

    // Persist; resolve true on success (form submit + close-confirm dialog).
    async function save(): Promise<SaveResult> {
        const fail = (msg: string): SaveResult => { setError(msg); return { ok: false, error: msg } }
        setStale(false)
        // Customer, product name and next date are `required` in the markup — one check for all.
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setBusy(true); setError('')
        // No currency: an abo is billed in the backoffice currency (Einstellungen → Allgemein).
        const common = { brand, customerId, cadence, mode }
        const manualFields = {
            items: [{ description: productName.trim(), quantity: 1, unitPrice: netPrice, taxRate: 0 }],
            startDate, nextBillingDate,
        }
        try {
            if (id) {
                const saved = await updateSubscription(id, { ...common, status, ...(manual ? manualFields : { items }), expectedUpdatedAt: loadedAt })
                setLoadedAt(saved.updatedAt) // a second save before the reload must not look stale
                publishChange('subscriptions', id); setBaseline(snapshot); setReloadKey((k) => k + 1); toast(T.saved)
            } else {
                await createSubscription({ ...common, ...manualFields })
                publishChange('subscriptions'); setBaseline(snapshot); toast(T.created); closeTab('subscription-new')
            }
            return { ok: true }
        } catch (e: any) {
            // Changed elsewhere since loading: nothing was saved, the input stays as it is.
            if (e?.response?.status === 409 && e?.response?.data?.code === 'stale') { setStale(true); return fail(SUBSCRIPTION_STALE) }
            return fail(e?.response?.data?.error ?? e?.message ?? T.saveFailed)
        } finally { setBusy(false) }
    }

    // „pausiert seit" / „beendet am" — empty for an abo paused or ended before the field existed.
    const sinceDay = parseCalendarDay(statusSince)
    const reactivating = manual && !!id && status === 'active' && savedStatus !== 'active'

    function submit(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && !locked && !readOnly, save)

    return (
        <form ref={formRef} noValidate onSubmit={submit} className="form-page">
            <div className="form-top">
            <div className="form-header">
                <h1>{readOnly ? T.titleView : id ? T.titleEdit : T.titleNew}</h1>
                {!readOnly && (
                    <button type="submit" disabled={disabled || !dirty || anyCustomer !== true}>
                        {busy ? '…' : id ? T.apply : T.create}
                    </button>
                )}
            </div>

            {readOnly && <div className="notice">{T.readOnly}</div>}

            {locked && (
                <div className="notice warn">
                    {T.lockedQueue}
                    {lock.position > 0 ? fill(T.lockedPosition, { position: lock.position }) : ''}.
                </div>
            )}
            {error && (
                <div className="notice err">
                    {error}
                    {/* Reload = the abo as it is now; the input on screen is given up on purpose. */}
                    {stale && <> <button type="button" className="link-btn" onClick={() => setReloadKey((k) => k + 1)}>{STALE_RELOAD}</button></>}
                </div>
            )}
            {lastRunError && (
                <div className="notice err">
                    {T.lastRunFailed}
                    {lastRunError.at ? fill(T.lastRunAt, { at: new Date(lastRunError.at).toLocaleString('de-DE') }) : ''}: {lastRunError.message}
                    {' '}{T.lastRunNotAdvanced}
                </div>
            )}
            {anyCustomer === false && <div className="notice err">{T.createCustomerFirst}</div>}
            </div>

            <fieldset disabled={disabled} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>
                <div className="panel">
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field field-wide"><label>{T.customer}</label>
                            <CustomerPicker value={customerId} onChange={setCustomerId} required /></div>
                        <div className="field" style={{ maxWidth: 200 }}><label>{T.brand}</label>
                            <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                                {BRANDS.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                            </select></div>
                    </div>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 160 }}><label>{T.cadence}</label>
                            <select value={cadence} onChange={(e) => setCadence(e.target.value as 'monthly' | 'yearly')}>
                                <option value="monthly">{T.monthly}</option>
                                <option value="yearly">{T.yearly}</option>
                            </select></div>
                        {/* 240, nicht 200: „auto (direkt finalisieren)" passte schon vorher knapp
                            nicht, und der Platz fuer den eigenen Pfeil (padding-right: 32px)
                            hat es enger gemacht. Ein Auswahlfeld, dessen Beschriftung
                            abgeschnitten ist, laesst den Betreiber raten, was er gewaehlt hat. */}
                        <div className="field" style={{ maxWidth: 240 }}><label>{T.mode}</label>
                            <select value={mode} onChange={(e) => setMode(e.target.value as 'auto' | 'draft')}>
                                <option value="auto">{T.modeAuto}</option>
                                <option value="draft">{T.modeDraft}</option>
                            </select></div>
                        {id && (
                            <div className="field" style={{ maxWidth: 220 }}><label>{T.status}</label>
                                <select value={status} disabled={!manual || disabled}
                                    onChange={(e) => setStatus(e.target.value as 'active' | 'paused' | 'ended')}>
                                    <option value="active">{T.statusActive}</option>
                                    <option value="paused">{T.statusPaused}</option>
                                    <option value="ended">{T.statusEnded}</option>
                                </select>
                                {!manual && (
                                    <small className="muted">{T.pspManaged}</small>
                                )}
                                {manual && status === savedStatus && sinceDay && status !== 'active' && (
                                    <small className="muted">{fill(SUBSCRIPTION_STATUS_SINCE[status], { date: formatDayDe(sinceDay) })}</small>
                                )}</div>
                        )}
                    </div>

                    {manual ? (
                        <>
                            <div className="row" style={{ alignItems: 'flex-start' }}>
                                <div className="field" style={{ maxWidth: 180 }}><label>{T.startDate}</label>
                                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
                                <div className="field" style={{ maxWidth: 220 }}><label>{T.nextBillingDate}</label>
                                    <input type="date" value={nextBillingDate} required
                                        onChange={(e) => { setNextBillingDate(e.target.value); setNextTouched(true) }} /></div>
                            </div>
                            {reactivating && <div className="notice warn" style={{ marginTop: 0 }}>{SUBSCRIPTION_REACTIVATE_HINT}</div>}
                            <p className="muted" style={{ marginTop: 0 }}>
                                {T.billingExplained}
                            </p>
                            <div className="row" style={{ alignItems: 'flex-start' }}>
                                <div className="field" style={{ flex: '1 1 30rem' }}><label>{T.productName}</label>
                                    <textarea rows={2} value={productName} required onChange={(e) => setProductName(e.target.value)} />
                                    <small className="muted">
                                        {T.placeholderLabel}<code>{ABO_PLACEHOLDER_START}</code>{T.placeholderStart}<code>{ABO_PLACEHOLDER_END}</code>{T.placeholderEnd}
                                        {preview && <>{T.nextInvoice}<b style={{ whiteSpace: 'pre-line' }}>{preview}</b></>}
                                    </small></div>
                                <div className="field" style={{ maxWidth: 160 }}><label>{T.netPrice}</label>
                                    <input type="number" step="0.01" min={0} value={netPrice} onChange={(e) => setNetPrice(Number(e.target.value))} />
                                    <small className="muted">{T.netPriceHint}</small></div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* PSP abos: the provider collects, anchored to the purchase date; our run skips them. */}
                            <div className="notice" style={{ marginTop: 0 }}>
                                {T.pspRunsBefore}<b>{source === 'mollie' ? T.pspMollie : T.pspStripe}</b>{T.pspRunsAfter}
                                {paidUntil
                                    ? <>{T.paidUntilBefore}<b>{new Date(paidUntil).toLocaleDateString('de-DE')}</b>{T.paidUntilAfter}</>
                                    : <>{T.noNextCollection}</>}
                            </div>
                            <h2 style={{ marginTop: 8 }}>{T.positions}</h2>
                            <LineItemsEditor items={items} onChange={setItems} />
                        </>
                    )}
                </div>
            </fieldset>
        </form>
    )
}
